import * as React from 'react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { css } from '@emotion/core';
import LocalDiv from '../../localization/LocalDiv';
import { Str } from '../../localization/translationKeys';
import { FLEX_COLUMN } from '../../layout/flexBox';
import { COLORS } from '../../globalStyle/colors';
import { useDispatch } from 'react-redux';
import { useMsgText } from '../../localization/hooks';
import { useLoading } from '../../core/api/hooks';
import { ActionType } from '../../core/redux/actionType';
import Msg from '../../localization/Msg';
import * as R from 'ramda';
import { updateDevPlanItemDescriptionAction } from './api/updateDevPlanItemDescriptionAction';
import { useAppState } from '../../core/redux/hooks';
import { TEXT_AREA } from '../../forms/styles/textarea';


const TEXT_AREA_ID     = 'item_desc_text_area';
const SAVE_DEBOUNCE_MS = 700;


const styles = {
  root:        css`
    background-color: ${COLORS.LIGHTEST_GREY};
    transition: opacity .5s ease;
    border-radius: 10px;
    padding: 30px;
  `,
  loadingForm: css`
    opacity: .7;
  `,
  inputGroup:  [
    FLEX_COLUMN,
    css`
      align-items: stretch;
    `,
  ],
  label:       css`
    align-self: flex-start;
    color: ${COLORS.DARK_GREY};
    font-size: 12px;
    line-height: 15px;
    font-weight: bold;
    margin-bottom: 5px;
  `,
  textArea:    [
    TEXT_AREA,
    css`
      width: 100%;
      font-size: 13px;
      background-color: ${COLORS.WHITE};
      ::placeholder {
        font-style: italic;
      }
    `,
  ],
};


/**
 * Returns the number of new lines in the given maybe string. If the maybe string isn't a string, then you get back 1.
 */
const calcLines = R.memoizeWith(
  (s: string | undefined): string => `${s}`,
  (s: string | undefined): number => s?.split('\n')?.length ?? 1,
);


type Props = {
  participantId: number;
  projectId: number;
  itemId: number;
  desc: string | undefined;
  className?: string;
};


/**
 * DevPlanDescText: renders a development item's description in an always-editable text area. Edits are auto-saved on a
 * debounce while typing and immediately on blur.
 */
const DevPlanDescText: React.FC<Props> = props => {
  const dispatch    = useDispatch();
  const placeholder = useMsgText(Str.DescriptionNotAdded);

  const [desc, setDesc] = useState<string>(props.desc || '');

  const loading          = useLoading(ActionType.UpdateDevPlanItemDescriptionRequest, props.itemId);
  const devPlanProjectId = useAppState(state => state.projectSummary?.devPlanProjectId || props.projectId);

  // Refs mirror state so stable callbacks read fresh values without re-binding every keystroke.
  const descRef       = useRef<string>(desc);
  const loadingRef    = useRef<boolean>(loading);
  const wasLoadingRef = useRef<boolean>(loading);
  // Tracks the value we most recently dispatched to the server. Used to skip no-op saves and to detect when the user
  // typed more during an in-flight save.
  const lastSavedRef  = useRef<string>(props.desc || '');
  const timeoutRef    = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { descRef.current = desc; },       [desc]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  const trySave = useCallback(
    () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (loadingRef.current) {
        // Defer: the loading-transition effect will retry once this save completes.
        return;
      }

      const value = descRef.current;
      if (value === lastSavedRef.current) {
        return;
      }

      lastSavedRef.current = value;
      dispatch(updateDevPlanItemDescriptionAction(props.participantId, devPlanProjectId, props.itemId, value));
    },
    [dispatch, props.participantId, devPlanProjectId, props.itemId],
  );

  // Reconcile prop -> local state only when the user has no pending edits. Without this gate, the redux update from our
  // own save would clobber a fresh keystroke.
  useEffect(
    () => {
      const propValue = props.desc || '';
      if (descRef.current === lastSavedRef.current && propValue !== descRef.current) {
        lastSavedRef.current = propValue;
        setDesc(propValue);
      }
    },
    [props.desc],
  );

  // When a save completes, if the user typed more during the in-flight window, save again.
  useEffect(
    () => {
      if (wasLoadingRef.current && !loading && descRef.current !== lastSavedRef.current) {
        trySave();
      }
      wasLoadingRef.current = loading;
    },
    [loading, trySave],
  );

  // Flush any pending debounced edit on unmount.
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (descRef.current !== lastSavedRef.current && !loadingRef.current) {
        const value = descRef.current;
        lastSavedRef.current = value;
        dispatch(updateDevPlanItemDescriptionAction(props.participantId, devPlanProjectId, props.itemId, value));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setDesc(event.target.value);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(trySave, SAVE_DEBOUNCE_MS);
    },
    [trySave],
  );

  const onBlur = useCallback(() => trySave(), [trySave]);

  return (
    <div css={[styles.root, loading && styles.loadingForm]} className={props.className}>
      <LocalDiv>
        <label css={styles.inputGroup}>
          <Msg str={Str.Description} css={styles.label} />
          <textarea
            id={TEXT_AREA_ID}
            placeholder={placeholder}
            rows={calcLines(desc)}
            value={desc}
            onChange={onChange}
            onBlur={onBlur}
            css={styles.textArea}
          />
        </label>
      </LocalDiv>
    </div>
  );
};

export default DevPlanDescText;
